import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Sector, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import axios from 'axios';
import './TestResultPage.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

// Function to detect LaTeX patterns
const hasLatex = (text) => {
  if (!text || typeof text !== 'string') {
    console.log(`No LaTeX: Text is ${text}`);
    return false;
  }
  // More specific LaTeX patterns to avoid false positives with standalone underscores
  const latexPatterns = [
    /\\([a-zA-Z]+)(?:\s|{)/, // Matches LaTeX commands like \frac, \sqrt, etc.
    /\^{[^}]+}/, // Matches superscripts like ^{2}
    /_{[^}]+}/, // Matches subscripts like _{n}
    /\\\[.*?\\\]/, // Matches display math like \[...\]
    /\\\(.*?\\\)/, // Matches inline math like \(...\)
    /\$.*?\$/, // Matches math enclosed in single dollar signs
  ];
  const result = latexPatterns.some(pattern => pattern.test(text));
  console.log(`Checking LaTeX in: "${text}" -> ${result}`);
  return result;
};

const renderTextWithLatex = (text, isBlock = false) => {
  if (!text || typeof text !== 'string') return text || 'Not available';

  if (hasLatex(text)) {
    try {
      // Remove dollar signs for KaTeX rendering, but do not escape underscores
      let cleanedText = text.replace(/(\$\$|\$)/g, '');
      return isBlock ? (
        <BlockMath math={cleanedText} />
      ) : (
        <InlineMath math={cleanedText} />
      );
    } catch (error) {
      console.error(`Error rendering LaTeX for text "${text}":`, error);
      return text; // Fallback to plain text if LaTeX rendering fails
    }
  }
  // Return plain text without modification for underscores
  return text;
};

const TestResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const backButtonRef = useRef(null);

  const { answers, questions, score, sectionStats, testName } = location.state || {};

  const [sectionWiseMarks, setSectionWiseMarks] = useState({});
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [testDetails, setTestDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const [totalAttempted, setTotalAttempted] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalIncorrect, setTotalIncorrect] = useState(0);
  const [notAttempted, setNotAttempted] = useState(0);

  const MARKS_PER_CORRECT = 5;
  const NEGATIVE_MARKING_PERCENTAGE = 0.1;

  const sectionNameMap = {
    'Section 1': 'Blood Relation',
    'Section 2': 'Arithmetic',
    'Section 3': 'Logic',
    'Section 4': 'Verbal Reasoning',
  };

  // Prevent browser back button functionality
  useEffect(() => {
    window.history.pushState(null, '', window.location.pathname);

    const handlePopState = (event) => {
      window.history.pushState(null, '', window.location.pathname);

      if (backButtonRef.current) {
        backButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        backButtonRef.current.style.animation = 'pulse 2s ease-in-out';
        backButtonRef.current.style.boxShadow = '0 0 20px #007bff';

        setTimeout(() => {
          if (backButtonRef.current) {
            backButtonRef.current.style.animation = '';
            backButtonRef.current.style.boxShadow = '';
          }
        }, 2000);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const getGrade = (percentage) => {
    if (percentage >= 80) return 'Excellent';
    if (percentage >= 60) return 'Good';
    if (percentage >= 40) return 'Average';
    if (percentage < 40) return 'Needs Improvement';
    return 'Average';
  };

  const getScoreColor = (percentage) => {
    if (percentage < 40) return '#FF6666';
    if (percentage >= 40 && percentage <= 60) return '#FFA500';
    return '#4CAF50';
  };

  useEffect(() => {
    if (questions && answers) {
      const totalQuestions = questions.length;
      let attempted = 0;
      let correct = 0;
      let incorrect = 0;
      const sectionData = {};

      questions.forEach(question => {
        const rawSection = question.section || 'General';
        const section = rawSection
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        if (!sectionData[section]) {
          sectionData[section] = { total: 0, correct: 0, incorrect: 0, skipped: 0 };
        }
        sectionData[section].total++;
      });

      questions.forEach((question, index) => {
        const userAnswer = answers[index];
        const rawSection = question.section || 'General';
        const section = rawSection
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        if (userAnswer !== undefined && userAnswer !== null && userAnswer !== '') {
          attempted++;
          if (userAnswer === question.correctAnswer) {
            correct++;
            sectionData[section].correct++;
          } else {
            incorrect++;
            sectionData[section].incorrect++;
          }
        } else {
          sectionData[section].skipped++;
        }
      });

      const notAttemptedCount = totalQuestions - attempted;
      const percentage = totalQuestions > 0 ? ((correct / totalQuestions) * 100).toFixed(2) : 0;

      setTotalAttempted(attempted);
      setTotalCorrect(correct);
      setTotalIncorrect(incorrect);
      setNotAttempted(notAttemptedCount);
      setOverallPercentage(percentage);
      setSectionWiseMarks(sectionData);
      setLoading(false);
    } else {
      setError('No test data available. Please retake the test.');
      setLoading(false);
    }
  }, [questions, answers]);

  useEffect(() => {
    const fetchTestDetails = async () => {
      try {
        const studentData = JSON.parse(localStorage.getItem('student'));
        if (studentData?.id) {
          setTestDetails({ name: testName || 'Unnamed Test' });
        }
      } catch (error) {
        console.error('Error fetching test details:', error);
        setTestDetails({ name: testName || 'Unnamed Test' });
      }
    };

    if (!loading && !error) {
      fetchTestDetails();
    }
  }, [loading, error, testName]);

  const handleBackToDashboard = () => {
    navigate('/studentmark-dashboard');
  };

  const handleViewQuestion = (question, userAnswer, isCorrect, isAnswered) => {
    setSelectedQuestion({
      questionText: question.questionText,
      userAnswer: userAnswer || 'Not Answered',
      correctAnswer: question.correctAnswer,
      status: !isAnswered ? 'Skipped' : isCorrect ? 'Correct' : 'Wrong',
      originalIndex: question.originalIndex
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuestion(null);
  };

  const pieData = Object.entries(sectionWiseMarks).map(([section, data]) => {
    const sectionName = sectionNameMap[section] || (section === 'General' ? 'General Knowledge' : section);
    return {
      name: sectionName,
      value: data.total ? parseFloat(((data.correct / data.total) * 100).toFixed(1)) : 0,
    };
  });

  const barData = [
    { name: 'Total Attempted', value: totalAttempted },
    { name: 'Not Attempted', value: notAttempted },
    { name: 'Total Correct', value: totalCorrect },
    { name: 'Total Incorrect', value: totalIncorrect },
  ];

  const COLORS = ['#66bfff', '#e6a6a6', '#8b9de6', '#9f87e6'];

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(-1);
  };

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 20}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        cornerRadius={5}
      />
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      // Check if the tooltip is for the BarChart by inspecting the data structure
      if (['Total Attempted', 'Not Attempted', 'Total Correct', 'Total Incorrect'].includes(data.name)) {
        return (
          <div
            style={{
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '4px 6px',
              fontSize: '11px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              fontFamily: "'Roboto', sans-serif"
            }}
          >
            <p style={{ margin: 0, lineHeight: '1.1', fontFamily: "'Roboto', sans-serif" }}>{`${data.name}: ${data.value}`}</p>
          </div>
        );
      }
      // For PieChart, show name and percentage
      return (
        <div
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '4px 6px',
            fontSize: '11px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            fontFamily: "'Roboto', sans-serif"
          }}
        >
          <p style={{ margin: 0, lineHeight: '1.1', fontFamily: "'Roboto', sans-serif" }}>{`${data.name}: ${data.value}%`}</p>
        </div>
      );
    }
    return null;
  };

  const questionsBySection = questions
    ? questions.reduce((acc, q, index) => {
        const section = q.section || 'General';
        if (!acc[section]) acc[section] = [];
        acc[section].push({ ...q, originalIndex: index });
        return acc;
      }, {})
    : {};

  if (loading) {
    return (
      <div className="loading-container" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <div className="loading-text" style={{ fontFamily: "'Roboto', sans-serif" }}>Loading test results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <div className="error-text" style={{ fontFamily: "'Roboto', sans-serif" }}>{error}</div>
        <button
          ref={backButtonRef}
          onClick={handleBackToDashboard}
          className="back-button"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const totalQuestions = questions ? questions.length : 0;

  return (
    <div className="test-result-container" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <div className="test-name-header" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <h3 style={{ fontFamily: "'Roboto', sans-serif" }}>{testDetails?.name || 'Test Results'}</h3>
      </div>

      <div className="score-header" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <h2 style={{ fontFamily: "'Roboto', sans-serif" }}>
          Your Score <span style={{ color: getScoreColor(overallPercentage), fontFamily: "'Roboto', sans-serif" }}>{overallPercentage}%</span>{' '}
          <span style={{ color: getScoreColor(overallPercentage), fontFamily: "'Roboto', sans-serif" }}>
            {overallPercentage >= 40 ? 'Pass' : 'Fail'}
          </span>
        </h2>
        <p style={{ fontFamily: "'Roboto', sans-serif" }}>Questions Answered: {totalAttempted} / {totalQuestions}</p>
      </div>

      <div className="main-content" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <div className="charts-section" style={{ fontFamily: "'Roboto', sans-serif" }}>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart onMouseLeave={onPieLeave}>
                <Pie
                  activeIndex={activeIndex}
                  activeShape={renderActiveShape}
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="40%"
                  cy="50%"
                  innerRadius="40%"
                  outerRadius="75%"
                  onMouseEnter={onPieEnter}
                  label={false}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{
                    marginRight: '10px',
                    maxWidth: '150px',
                    fontFamily: "'Roboto', sans-serif"
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data-text" style={{ fontFamily: "'Roboto', sans-serif" }}>No data available for Pie Chart</p>
          )}

          <div className="bar-chart-section" style={{ fontFamily: "'Roboto', sans-serif" }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={barData}
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                className="no-hover-bar"
              >
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tick={{ fontSize: '11px', fontFamily: "'Roboto', sans-serif" }}
                />
                <YAxis
                  tick={{ fontFamily: "'Roboto', sans-serif" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="value"
                  fill="#8884d8"
                  barSize={20}
                  isAnimationActive={false}
                  cursor="default"
                  onMouseEnter={() => {}}
                  onMouseLeave={() => {}}
                  onClick={() => {}}
                  isInteractive={false}
                >
                  {barData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="tables-section" style={{ fontFamily: "'Roboto', sans-serif" }}>
          <div className="section-table" style={{ fontFamily: "'Roboto', sans-serif" }}>
            <table className="table-base">
              <thead>
                <tr className="table-header">
                  <th className="th-base">Section</th>
                  <th className="th-base">Total</th>
                  <th className="th-base">Correct</th>
                  <th className="th-base">%</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(sectionWiseMarks).length > 0 ? (
                  Object.entries(sectionWiseMarks).map(([section, data], index) => {
                    const percentage = data.total ? ((data.correct / data.total) * 100).toFixed(1) : 0;
                    return (
                      <tr key={section} className={index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                        <td className="td-base">{sectionNameMap[section] || section}</td>
                        <td className="td-base">{data.total}</td>
                        <td className="td-base">{data.correct}</td>
                        <td className="td-base">{percentage}%</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="td-base no-data-text">
                      No section-wise data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="metrics-table" style={{ fontFamily: "'Roboto', sans-serif" }}>
            <table className="table-base">
              <thead>
                <tr className="table-header">
                  <th className="th-base">Metric</th>
                  <th className="th-base">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="table-row-even">
                  <td className="td-base">Total Attempted</td>
                  <td className="td-base">{totalAttempted}/{totalQuestions}</td>
                </tr>
                <tr className="table-row-odd">
                  <td className="td-base">Not Attempted</td>
                  <td className="td-base">{notAttempted}/{totalQuestions}</td>
                </tr>
                <tr className="table-row-even">
                  <td className="td-base">Total Correct</td>
                  <td className="td-base">{totalCorrect}/{totalQuestions}</td>
                </tr>
                <tr className="table-row-odd">
                  <td className="td-base">Total Incorrect</td>
                  <td className="td-base">{totalIncorrect}/{totalQuestions}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="legend-container" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <div className="legend-box" style={{ fontFamily: "'Roboto', sans-serif" }}>
          Test Status: <span className="highlight-box" style={{ fontFamily: "'Roboto', sans-serif" }}>Completed</span>
        </div>
        <div className="legend-box" style={{ fontFamily: "'Roboto', sans-serif" }}>
          Grade: <span className="highlight-box" style={{ fontFamily: "'Roboto', sans-serif" }}>{getGrade(overallPercentage)}</span>
        </div>
      </div>

      <div className="question-details-section" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <h3 style={{ fontFamily: "'Roboto', sans-serif" }}>Question Details</h3>
        <table className="question-details-table">
          <thead>
            <tr className="table-header">
              <th className="th-base">Questions</th>
              <th className="th-base">Given Answer</th>
              <th className="th-base">Correct Answer</th>
              <th className="th-base">Status</th>
              <th className="th-base">View</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(questionsBySection).length > 0 ? (
              Object.entries(questionsBySection).map(([section, sectionQuestions], sectionIndex) => (
                <React.Fragment key={`section-${section}`}>
                  <tr
                    className={`section-header-row ${sectionIndex > 0 ? 'section-border-top' : ''}`}
                    style={{ fontFamily: "'Roboto', sans-serif" }}
                  >
                    <td colSpan="5" className="td-base">
                      {sectionNameMap[section] || section}
                    </td>
                  </tr>
                  {sectionQuestions.map((q, idx) => {
                    const userAnswer = answers[q.originalIndex];
                    const isCorrect = userAnswer === q.correctAnswer;
                    const isAnswered = userAnswer !== undefined && userAnswer !== null && userAnswer !== '';
                    return (
                      <tr
                        key={q.originalIndex}
                        className={idx % 2 === 0 ? 'question-row-even' : 'question-row-odd'}
                        style={{ fontFamily: "'Roboto', sans-serif" }}
                      >
                        <td className="td-base question-text">
                          Q{q.originalIndex + 1}. {renderTextWithLatex(q.questionText, false)}
                        </td>
                        <td className="td-base answer-text">
                          {renderTextWithLatex(userAnswer || 'Not Answered', false)}
                        </td>
                        <td className="td-base answer-text">
                          {renderTextWithLatex(q.correctAnswer, false)}
                        </td>
                        <td className={`td-base ${!isAnswered ? 'status-skipped' : isCorrect ? 'status-correct' : 'status-wrong'}`}>
                          {!isAnswered ? 'Skipped' : isCorrect ? 'Correct' : 'Wrong'}
                        </td>
                        <td className="td-base" style={{ textAlign: 'center' }}>
                          <button className="view-button" onClick={() => handleViewQuestion(q, userAnswer, isCorrect, isAnswered)} title="View" aria-label="View question">
                            <VisibilityIcon style={{ fontSize: '17px', color: '#cc8888', border: 'none' }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="td-base no-data-text">
                  No questions available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && selectedQuestion && (
        <div className="modal-overlay">
          <div style={viewModal}>
            <h3 style={viewModalHeader}>Question Details</h3>
            <div style={viewModalContent}>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Question {selectedQuestion.originalIndex + 1}</div>
                <div style={{ ...viewModalValue, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithLatex(selectedQuestion.questionText, true)}</div>
              </div>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Your Answer</div>
                <div style={{ ...viewModalValue, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithLatex(selectedQuestion.userAnswer, true)}</div>
              </div>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Correct Answer</div>
                <div style={{ ...viewModalValue, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithLatex(selectedQuestion.correctAnswer, true)}</div>
              </div>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Status</div>
                <div style={viewModalValue}>
                  <span style={{ color: selectedQuestion.status === 'Correct' ? '#4CAF50' : selectedQuestion.status === 'Wrong' ? '#FF6666' : '#FFA500', fontWeight: 'bold' }}>
                    {selectedQuestion.status}
                  </span>
                </div>
              </div>
            </div>
            <button style={viewModalCloseButton} onClick={handleCloseModal}>Close</button>
          </div>
        </div>
      )}

      <div className="back-button-container" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <button
          ref={backButtonRef}
          onClick={handleBackToDashboard}
          className="back-button"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

const tdStyle = { padding: '8px', border: '1px solid #ddd', fontSize: '12px', fontFamily: "'Roboto', sans-serif", textAlign: 'center' };
const thStyle = { padding: '10px', fontWeight: 'bold', textAlign: 'center', border: '1px solid #ddd', fontSize: '14px', fontFamily: "'Roboto', sans-serif" };
const legendBoxStyle = { fontSize: '16px', fontFamily: "'Roboto', sans-serif" };
const highlightBoxStyle = { display: 'inline-block', backgroundColor: '#4CAF50', color: 'white', padding: '2px 10px', borderRadius: '5px', marginLeft: '6px', fontFamily: "'Roboto', sans-serif" };
const backButtonStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 16px', fontSize: '14px', fontFamily: "'Roboto', sans-serif", fontWeight: '500', color: '#6B21A8', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer', borderRadius: '30px', border: '2px solid transparent', backgroundImage: 'linear-gradient(#ffffff,#ffffff), linear-gradient(to right,#FFBBBB,#A8C1FF,#C5A8FF)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', transition: 'all 0.4s ease' };
const viewModal = {
  backgroundColor: 'white',
  borderRadius: '0.5rem',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  color: '#1F2937',
  fontFamily: "'Roboto', sans-serif",
  width: '90vw',
  maxWidth: '800px',
  margin: '0 auto',
  padding: '24px',
  maxHeight: '81vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  position: 'relative',
};
const viewModalHeader = { 
  fontSize: '20px', 
  fontWeight: '600', 
  color: '#6B21A8',
  marginBottom: '16px',
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: '8px',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalContent = { 
  fontSize: '14px', 
  color: '#1F2937', 
  lineHeight: '1.6',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalSection = {
  paddingBottom: '12px',
  borderBottom: '1px solid #e5e7eb',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalLabel = { 
  fontWeight: '600', 
  color: '#6B21A8',
  marginBottom: '4px',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalValue = {
  color: '#1F2937',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalCloseButton = { 
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px 15px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#6B21A8',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  cursor: 'pointer',
  borderRadius: '30px',
  border: '2px solid transparent',
  backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  transition: 'all 0.4s ease',
  alignSelf: 'flex-end',
  marginTop: '16px',
  fontFamily: "'Roboto', sans-serif",
};

export default TestResultPage;